import { AxiosInstance } from "axios";

import { authHeader } from "src/api/authHeader";
import { isRefreshUrl, isTaktileRequest } from "src/api/middlewares/utils";
import { TokenWithUserAndKey } from "src/clients/taktile-api";
import { useAuthStore } from "src/store/AuthStore";
import {
  calculateTokenExpiration,
  getCurrentUnixTimestamp,
} from "src/utils/calculateTokenExpiration";

export const addAuthorizationHeaderMiddleware = (
  axiosInstance: AxiosInstance,
) => {
  // Concurrent requests that all see a near-expiry token share one refresh
  // instead of firing one /login/refresh each.
  let refreshInFlight: Promise<TokenWithUserAndKey | void> | null = null;
  const refreshSession = (): Promise<TokenWithUserAndKey | void> => {
    if (refreshInFlight === null) {
      refreshInFlight = useAuthStore
        .getState()
        .actions.refresh()
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  };

  return axiosInstance.interceptors.request.use(
    async (config) => {
      const url = [config.baseURL ?? "", config.url ?? ""].join("");

      if (url && isTaktileRequest(url) && !config.headers?.["Authorization"]) {
        // path-only match: the scoped refresh URL carries query params
        const isRefreshRequest = isRefreshUrl(url);
        if (isRefreshRequest) {
          config.headers.set(authHeader(true));
        } else {
          // Check the expiration of the ID token
          const idTokenHeader = authHeader(false);
          if ("Authorization" in idTokenHeader) {
            const tokenExpiry = calculateTokenExpiration(idTokenHeader);
            // If the token is expiring in 1 minute or less, refresh it.
            if (tokenExpiry - getCurrentUnixTimestamp() < 60) {
              await refreshSession();
            }
          }

          config.headers.set(authHeader(false));
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};
